import {
    ChatBubbleLeftIcon,
    EnvelopeIcon,
    MapPinIcon,
    PhoneArrowUpRightIcon,
    Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { TouchpointKind } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";
import {
    asCadenceUnitInput,
    asTouchpointKinds,
    cadenceUnitOptions,
    EditableTouchpoint,
    emptyEditableTouchpoint,
    isAnySelected,
    parseCadenceCount,
    suggestedTouchpointsForTier,
    summarizeSchedule,
    toggleAny,
    toggleKind,
} from "./touchpoint-cadence";

type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["agencyPlaybook"]
>["touchpoints"][number];

type ChannelOption = {
    id: TouchpointKind;
    label: string;
    icon: React.ComponentType<React.ComponentProps<"svg">>;
};

const channelOptions: ChannelOption[] = [
    { id: "email", label: "Email", icon: EnvelopeIcon },
    { id: "call", label: "Call", icon: PhoneArrowUpRightIcon },
    { id: "text", label: "Text", icon: ChatBubbleLeftIcon },
];

const visitChannelOption: ChannelOption = {
    id: "visit",
    label: "Visit",
    icon: MapPinIcon,
};

const ANY_OPTION_ID = "any";

const selectedLabels = (options: ChannelOption[], kinds: TouchpointKind[]): string[] => {
    if (
        isAnySelected(
            kinds,
            options.map(option => option.id),
        )
    ) {
        return ["Any"];
    }
    return options.filter(option => kinds.includes(option.id)).map(o => o.label);
};

const fromTouchpoint = (touchpoint: Touchpoint): EditableTouchpoint => ({
    kinds: asTouchpointKinds(touchpoint.kinds),
    intervalUnit: asCadenceUnitInput(touchpoint.intervalUnit),
    intervalCount: touchpoint.intervalCount,
});

interface EditCardProps {
    options: ChannelOption[];
    value: EditableTouchpoint;
    onChange: (value: EditableTouchpoint) => void;
    footer: React.ReactNode;
}

const TouchpointEditCard: React.FC<EditCardProps> = ({
    options,
    value,
    onChange,
    footer,
}) => {
    const noKindsSelected = value.kinds.length === 0;
    const availableKinds = options.map(option => option.id);
    const anySelected = isAnySelected(value.kinds, availableKinds);

    //adding in the "Any" UI helper to the default options, and displaying it as selected if each individual option was selected.
    const buttonOptions: ButtonSelectOption[] = [
        { id: ANY_OPTION_ID, label: "Any", icon: Squares2X2Icon },
        ...options,
    ];
    const selectedIds = anySelected ? [ANY_OPTION_ID, ...value.kinds] : value.kinds;

    return (
        <div className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
                <ButtonSelect
                    options={buttonOptions}
                    selectedIds={selectedIds}
                    onToggle={id =>
                        onChange({
                            ...value,
                            kinds:
                                id === ANY_OPTION_ID
                                    ? toggleAny(value.kinds, availableKinds)
                                    : toggleKind(value.kinds, id as TouchpointKind),
                        })
                    }
                />
                <span className="text-sm text-gray-600">every</span>
                <div className="w-20">
                    <AutosavingInput
                        type="number"
                        min={1}
                        initial={String(value.intervalCount)}
                        onCommit={input => {
                            const count = parseCadenceCount(input);
                            if (count !== null) {
                                onChange({ ...value, intervalCount: count });
                            }
                        }}
                    />
                </div>
                <div className="w-28">
                    <Select
                        textSize="sm"
                        value={value.intervalUnit}
                        onChange={e =>
                            onChange({
                                ...value,
                                intervalUnit: asCadenceUnitInput(e.target.value),
                            })
                        }>
                        {cadenceUnitOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>
            {noKindsSelected ? (
                <div className="text-sm text-amber-600">
                    Pick at least one touchpoint type for this schedule.
                </div>
            ) : null}
            {footer}
        </div>
    );
};

interface SavedRowProps {
    touchpoint: Touchpoint;
    options: ChannelOption[];
    onSave: (id: string, value: EditableTouchpoint) => void;
    onDelete: (id: string) => void;
}

const SavedTouchpointRow: React.FC<SavedRowProps> = ({
    touchpoint,
    options,
    onSave,
    onDelete,
}) => {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState<EditableTouchpoint>(() =>
        fromTouchpoint(touchpoint),
    );

    if (editing) {
        return (
            <TouchpointEditCard
                options={options}
                value={draft}
                onChange={setDraft}
                footer={
                    <div className="flex items-center gap-2">
                        <PrimaryCompactButton
                            type="button"
                            disabled={draft.kinds.length === 0}
                            onClick={() => {
                                onSave(touchpoint.id, draft);
                                setEditing(false);
                            }}>
                            Save
                        </PrimaryCompactButton>
                        <SecondaryCompactButton
                            type="button"
                            onClick={() => setEditing(false)}>
                            Cancel
                        </SecondaryCompactButton>
                        <div className="ml-auto">
                            <DeleteButton
                                backgroundColor="bg-gray-100"
                                size="sm"
                                onClick={() => onDelete(touchpoint.id)}
                            />
                        </div>
                    </div>
                }
            />
        );
    }

    const labels = selectedLabels(options, asTouchpointKinds(touchpoint.kinds));
    return (
        <div className="flex items-center gap-1">
            <span className="text-sm text-gray-700">
                {summarizeSchedule(
                    labels,
                    touchpoint.intervalCount,
                    asCadenceUnitInput(touchpoint.intervalUnit),
                )}
            </span>
            <EditButton
                backgroundColor="bg-white"
                size="sm"
                onClick={() => {
                    setDraft(fromTouchpoint(touchpoint));
                    setEditing(true);
                }}
            />
        </div>
    );
};

interface NewRowProps {
    options: ChannelOption[];
    initial: EditableTouchpoint;
    onCreate: (value: EditableTouchpoint) => void;
    onCancel: () => void;
}

const NewTouchpointRow: React.FC<NewRowProps> = ({
    options,
    initial,
    onCreate,
    onCancel,
}) => {
    const [draft, setDraft] = React.useState<EditableTouchpoint>(initial);
    return (
        <TouchpointEditCard
            options={options}
            value={draft}
            onChange={setDraft}
            footer={
                <div className="flex items-center gap-2">
                    <PrimaryCompactButton
                        type="button"
                        disabled={draft.kinds.length === 0}
                        onClick={() => onCreate(draft)}>
                        Save
                    </PrimaryCompactButton>
                    <SecondaryCompactButton type="button" onClick={onCancel}>
                        Cancel
                    </SecondaryCompactButton>
                </div>
            }
        />
    );
};

interface NewRow {
    key: string;
    initial: EditableTouchpoint;
}

interface Props {
    touchpoints: Touchpoint[];
    newRows: NewRow[];
    tierKey: string;
    visitsEnabled: boolean;
    onAddRow: (initial: EditableTouchpoint) => void;
    onSaveTouchpoint: (id: string, value: EditableTouchpoint) => void;
    onDeleteTouchpoint: (id: string) => void;
    onCreateTouchpoint: (newRowKey: string, value: EditableTouchpoint) => void;
    onCancelNewRow: (newRowKey: string) => void;
}

export const TierTouchpointsEditor: React.FC<Props> = ({
    touchpoints,
    newRows,
    tierKey,
    visitsEnabled,
    onAddRow,
    onSaveTouchpoint,
    onDeleteTouchpoint,
    onCreateTouchpoint,
    onCancelNewRow,
}) => {
    const options = visitsEnabled
        ? [...channelOptions, visitChannelOption]
        : channelOptions;
    const isEmpty = touchpoints.length === 0 && newRows.length === 0;
    const suggestions = suggestedTouchpointsForTier(
        tierKey,
        options.map(option => option.id),
    );
    return (
        <div className="space-y-2 pl-1">
            <div className="text-sm font-medium text-gray-500">Touchpoints</div>
            {isEmpty ? (
                <div className="text-sm text-gray-400">
                    Marketers will be prompted to reach out to their assigned referral
                    sources in this tier based on the touchpoints you schedule.
                </div>
            ) : (
                <div className="space-y-2">
                    {touchpoints.map(touchpoint => (
                        <SavedTouchpointRow
                            key={touchpoint.id}
                            touchpoint={touchpoint}
                            options={options}
                            onSave={onSaveTouchpoint}
                            onDelete={onDeleteTouchpoint}
                        />
                    ))}
                    {newRows.map(row => (
                        <NewTouchpointRow
                            key={row.key}
                            options={options}
                            initial={row.initial}
                            onCreate={value => onCreateTouchpoint(row.key, value)}
                            onCancel={() => onCancelNewRow(row.key)}
                        />
                    ))}
                </div>
            )}
            <div className="flex items-center gap-2">
                <SecondaryButton onClick={() => onAddRow(emptyEditableTouchpoint())}>
                    Add touchpoint
                </SecondaryButton>
                {isEmpty && suggestions.length > 0 ? (
                    <SecondaryButton onClick={() => suggestions.forEach(onAddRow)}>
                        Use recommended schedule
                    </SecondaryButton>
                ) : null}
            </div>
        </div>
    );
};
